import React from 'react';
import { useSectionAccent } from './TrackerSection';
import { formatCompactCurrency } from '../../../utils/formatters';

/**
 * The Pipeline Tracker's shared surface, matched to the design language the
 * mockups already established (docs/mockups/discharges-page-redesign.html):
 * six cards in a three-by-three grid, each with its own colour and icon, a
 * three pixel top accent, a tinted gradient body, an uppercase label, and a
 * large value with a muted sub-line.
 *
 * These were duplicated badly when the Upload, Lead source, Compliance and
 * Trends tabs were written: they reached for slate-* and flat bordered boxes
 * while the rest of the product used this. The result read as a different
 * application on alternate tabs.
 */

export const CARD = 'rounded-lg border border-gray-700 bg-gray-800/40';

/** Brand blue, per the design tokens. */
export const BRAND_BLUE = '#2563eb';

/**
 * Abbreviated money, from cents.
 *
 * $123,613,000 in a stat card is a number nobody reads; $123.6M is. The full
 * figure stays available in the tables, where a reader is comparing rather than
 * scanning. The app-wide three-significant-figure rule (formatters.ts).
 */
export function shortMoney(cents: number | null | undefined): string {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) return '-';
  return formatCompactCurrency(cents);
}

/** Abbreviated counts, so a six figure deal count does not blow the card width. */
export function shortCount(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '-';
  const a = Math.abs(n);
  if (a >= 1e6) return `${(n / 1e6).toFixed(1).replace(/\.0$/, '')}M`;
  if (a >= 10_000) return `${(n / 1e3).toFixed(0)}K`;
  return n.toLocaleString('en-AU');
}

const hexRgb = (hex: string): string => {
  const h = hex.replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
};

export interface StatCardProps {
  label: string;
  value: string;
  sub?: string;
  /** Lucide icon component. Every card carries its own. */
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  /** Card accent colour. Drives the tint, the rule and the icon badge. None: the section's colour. */
  color?: string;
  /** Tints the VALUE only, for figures that carry a warning of their own. */
  tone?: 'plain' | 'warn' | 'alert' | 'good';
}

export const StatCard: React.FC<StatCardProps> = ({
  label, value, sub, icon: Icon, color: ownColor, tone = 'plain',
}) => {
  const section = useSectionAccent();
  const color = ownColor ?? section ?? BRAND_BLUE;
  const rgb = hexRgb(color);
  const valueColor =
    tone === 'alert' ? 'text-red-400'
      : tone === 'warn' ? 'text-amber-400'
        : tone === 'good' ? 'text-emerald-400' : 'text-white';

  return (
    <div className="relative overflow-hidden rounded-lg border border-gray-700/80 shadow-lg" data-accent={color}>
      <div
        className="absolute inset-0"
        style={{
          background:
            `linear-gradient(135deg, rgba(${rgb},0.15) 0%, rgba(${rgb},0.05) 50%, rgba(17,24,39,0.85) 100%)`,
        }}
      />
      <div
        className="absolute inset-x-0 top-0 h-[3px]"
        style={{ background: `linear-gradient(90deg, transparent, ${color} 15%, ${color} 85%, transparent)` }}
      />
      <div
        className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-lg"
        style={{
          background: `linear-gradient(135deg, ${color}30, ${color}10)`,
          boxShadow: `0 0 20px ${color}20`,
          color,
        }}
      >
        <Icon className="h-5 w-5" />
      </div>
      <div className="relative z-10 p-5">
        <div className="pr-12 text-xs font-semibold uppercase tracking-wider text-gray-400">{label}</div>
        <div className={`mt-2 text-3xl font-bold tracking-tight ${valueColor}`}>{value}</div>
        {sub && <div className="mt-2 text-xs text-gray-400">{sub}</div>}
      </div>
    </div>
  );
};

/** Six cards, three per row: the layout the mockups settled on. Five looks unfinished. */
/**
 * `six` lays six cards out with no orphan: one row on a wide screen, three by two on a laptop,
 * two by three on a tablet. Three columns stays the default for three or five cards.
 */
export const StatGrid: React.FC<{ children: React.ReactNode; six?: boolean }> = ({ children, six }) => (
  <div className={`grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 ${six ? '2xl:grid-cols-6' : ''}`}>{children}</div>
);

export const SectionHeading: React.FC<{
  icon: React.ComponentType<{ className?: string; color?: string }>;
  title: string;
}> = ({ icon: Icon, title }) => (
  <div className="mb-3 flex items-center gap-2">
    <Icon className="h-4 w-4" color={BRAND_BLUE} />
    <h3 className="text-sm font-semibold text-gray-200">{title}</h3>
  </div>
);

export const InfoBanner: React.FC<{ children: React.ReactNode; note?: React.ReactNode }> = ({ children, note }) => (
  <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 p-4">
    <p className="text-sm text-blue-100">{children}</p>
    {note && <p className="mt-1 text-xs text-blue-200/70">{note}</p>}
  </div>
);

export const EmptyState: React.FC<{ title: string; body: React.ReactNode }> = ({ title, body }) => (
  <div className={`${CARD} p-8 text-center`}>
    <p className="text-gray-200">{title}</p>
    <p className="mx-auto mt-1 max-w-xl text-sm text-gray-400">{body}</p>
  </div>
);

export const WarnState: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-200">
    {children}
  </div>
);

export const TH: React.FC<{ children: React.ReactNode; right?: boolean }> = ({ children, right }) => (
  <th className={`px-4 py-2 font-medium ${right ? 'text-right' : ''}`}>{children}</th>
);
